import * as React from "react";
import { useState, useMemo } from "react";
import { usePermissions, useSidebarState } from "react-admin";
import { useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  styled,
  Collapse,
  ListItemIcon,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { menuItems } from "./menuConfig";

// Custom styled ListItem with hover and active effects
const StyledListItem = styled(ListItem)(({ theme, isActive }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: "white",
    borderRadius: theme.shape.borderRadius,
  },
  backgroundColor: isActive ? "white" : "transparent",
  color: isActive ? "#915122" : "white",
  transition: "background-color 0.2s ease, border-radius 0.2s ease",
  paddingLeft: theme.spacing(4),
  "&.Mui-selected": {
    backgroundColor: theme.palette.action.selected,
  },
  borderRadius: "5px",
}));

// Style for submenu items
const SubMenuItem = styled(ListItem)(({ theme, isActive }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: "white",
    borderRadius: theme.shape.borderRadius,
  },
  backgroundColor: isActive ? "white" : "transparent",
  color: isActive ? "#915122" : "white",
  paddingLeft: theme.spacing(6),
  borderRadius: "5px",
}));

// Style for nested submenu items (second level)
const NestedSubMenuItem = styled(ListItem)(({ theme, isActive }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: "white",
    borderRadius: theme.shape.borderRadius,
  },
  backgroundColor: isActive ? "white" : "transparent",
  color: isActive ? "#915122" : "white",
  paddingLeft: theme.spacing(8),
  borderRadius: "5px",
}));



const Menu = ({ dense = false }) => {
  const { permissions } = usePermissions();
  const [open] = useSidebarState();
  const location = useLocation();
  
  // State for open submenus
  const [openMenus, setOpenMenus] = useState({});
  
  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    if (!permissions) return [];
    
    return Object.values(menuItems).filter(item => {
      // Check if user has permission for this item
      return item.permissions.includes(permissions);
    });
  }, [permissions]);
  
  // Toggle submenu
  const toggleMenu = (menuId) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };
  
  // Check if a menu item is active
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  // Check if any child is active for nested menus
  const isParentActive = (children) => {
    if (!children) return false;
    
    for (const child of children) {
      if (child.href && isActive(child.href.replace('/#', ''))) {
        return true;
      }
      // Recursively check nested children
      if (child.children && isParentActive(child.children)) {
        return true;
      }
    }
    
    return false;
  };
  
  // Render nested submenu items
  const renderNestedSubmenu = (item, level = 1) => {
    const isItemActive = item.href ? isActive(item.href.replace('/#', '')) : false;
    const hasChildren = item.children && item.children.length > 0;
    const MenuItemComponent = level === 1 ? SubMenuItem : NestedSubMenuItem;
    
    if (hasChildren) {
      return (
        <React.Fragment key={item.id}>
          <MenuItemComponent 
            button 
            onClick={() => toggleMenu(item.id)}
            isActive={isItemActive || isParentActive(item.children)}
          >
            <ListItemText primary={item.label} />
            {openMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
          </MenuItemComponent>
          
          <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderNestedSubmenu(child, level + 1))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }
    
    return (
      <MenuItemComponent
        key={item.id}
        button
        component="a"
        href={item.href}
        isActive={isItemActive}
      >
        <ListItemText primary={item.label} />
      </MenuItemComponent>
    );
  };
  
  // Render a main menu item
  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isItemActive = item.href ? isActive(item.href.replace('/#', '')) : false;
    const isParentItemActive = isParentActive(item.children);
    
    // Filter children based on permissions
    const filterChildren = (children) => {
      if (!children) return [];
      
      return children.filter(child => {
        // Check if child has specific permissions
        if (child.permissions && !child.permissions.includes(permissions)) {
          return false;
        }
        
        // Recursively filter nested children
        if (child.children) {
          child.children = filterChildren(child.children);
          // Keep parent even if all children are filtered out
          return child.children.length > 0 || child.href;
        }
        
        return true;
      });
    };
    
    const filteredChildren = filterChildren(item.children);
    
    if (item.children && filteredChildren.length > 0) {
      return (
        <React.Fragment key={item.id}>
          <StyledListItem 
            button 
            onClick={() => toggleMenu(item.id)}
            isActive={isItemActive || isParentItemActive}
          >
            {Icon && (
              <ListItemIcon sx={{ color: "inherit", minWidth: "36px" }}>
                <Icon />
              </ListItemIcon>
            )}
            <ListItemText primary={item.label} />
            {openMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
          </StyledListItem>
          
          <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {filteredChildren.map(child => renderNestedSubmenu(child))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }
    
    return (
      <StyledListItem
        key={item.id}
        button
        component="a"
        href={item.href}
        isActive={isItemActive}
      >
        {Icon && (
          <ListItemIcon sx={{ color: "inherit", minWidth: "36px" }}>
            <Icon />
          </ListItemIcon>
        )}
        <ListItemText primary={item.label} />
      </StyledListItem>
    );
  };
  
  // Special case for supervisor and knowledgeManger (single items)
  if (permissions === "supervisor") {
    return (
      <List sx={{
        width: "100%",
        bgcolor: "primary.main",
        color: "white",
        borderRadius: 0,
        paddingRight: 2,
      }}>
        <StyledListItem
          button
          component="a"
          href="/#/auctions"
          isActive={isActive('/auctions')}
        >
          <ListItemText primary="Auctions" />
        </StyledListItem>
        <StyledListItem
          button
          component="a"
          href="/#/salat-mahsool/products"
          isActive={isActive('/salat-mahsool/products')}
        >
          <ListItemText primary="Salat Mahsool Products" />
        </StyledListItem>
        <StyledListItem
          button
          component="a"
          href="/#/salat-mahsool/categories"
          isActive={isActive('/salat-mahsool/categories')}
        >
          <ListItemText primary="Salat Mahsool Categories" />
        </StyledListItem>
        <StyledListItem
          button
          component="a"
          href="/#/salat-mahsool/orders"
          isActive={isActive('/salat-mahsool/orders')}
        >
          <ListItemText primary="Salat Mahsool Orders" />
        </StyledListItem>
      </List>
    );
  }
  
  if (permissions === "knowledgeManger") {
    return (
      <List sx={{
        width: "100%",
        bgcolor: "primary.main",
        color: "white",
        borderRadius: 0,
        paddingRight: 2,
      }}>
        <StyledListItem
          button
          component="a"
          href="/#/knowledgemanager"
          isActive={isActive('/knowledgemanager')}
        >
          <ListItemText primary="Knowledge Manager" />
        </StyledListItem>
      </List>
    );
  }
  
  return (
    <List sx={{
      width: "100%",
      bgcolor: "primary.main",
      color: "white",
      borderRadius: 0,
      paddingRight: 2,
    }}>
      {filteredMenuItems.map(renderMenuItem)}
    </List>
  );
};

export default Menu;